import { approvalService, type LeaveDetails, type ApprovalRequestWithRelations, type LeaveRequestWithApplicant } from '../services/approval'
import { authStore } from '../store/authStore'

// Re-export types for backward compatibility
export type { LeaveRequestWithApplicant } from '../services/approval'

// Helper to filter and cast leave requests
function toLeaveRequest(request: ApprovalRequestWithRelations): ApprovalRequestWithRelations | null {
  if (request.type !== 'leave') return null
  return request
}

export const leavesApi = {
  // Get all leave requests
  async getLeaveRequests(options?: { 
    userId?: string
    status?: 'pending' | 'in_progress' | 'approved' | 'rejected'
    limit?: number 
  }): Promise<LeaveRequestWithApplicant[]> {
    const requests = await approvalService.getApprovalRequests({
      type: 'leave',
      status: options?.status,
      applicantId: options?.userId,
      limit: options?.limit
    })
    
    return requests
      .map(toLeaveRequest)
      .filter((r): r is LeaveRequestWithApplicant => r !== null) as LeaveRequestWithApplicant[]
  },

  // Get single leave request
  async getLeaveRequestById(id: string): Promise<LeaveRequestWithApplicant | null> {
    const request = await approvalService.getApprovalRequestById(id)
    if (!request || request.type !== 'leave') return null
    return toLeaveRequest(request) as LeaveRequestWithApplicant
  },

  // Create new leave request
  async createLeaveRequest(leave: {
    title: string
    leave_type: 'annual' | 'sick' | 'personal' | 'other'
    start_date: string
    end_date: string
    total_days: number
    reason?: string
  }): Promise<ApprovalRequestWithRelations> {
    const user = authStore.getState().user
    if (!user) throw new Error('User not authenticated')

    const details: LeaveDetails = {
      leave_type: leave.leave_type,
      start_date: leave.start_date,
      end_date: leave.end_date,
      total_days: leave.total_days,
      reason: leave.reason
    }

    const request = await approvalService.createLeaveRequest(
      user.id,
      leave.title,
      details
    )

    return request
  },

  // Update leave request
  async updateLeaveRequest(id: string, updates: {
    title?: string
    status?: 'pending' | 'in_progress' | 'approved' | 'rejected'
    leave_type?: 'annual' | 'sick' | 'personal' | 'other'
    start_date?: string
    end_date?: string
    total_days?: number
    reason?: string
  }): Promise<ApprovalRequestWithRelations> {
    // Get current request to merge details
    const current = await approvalService.getApprovalRequestById(id)
    if (!current || current.type !== 'leave') {
      throw new Error('Leave request not found')
    }

    const updateData: Parameters<typeof approvalService.updateApprovalRequest>[1] = {}
    
    if (updates.title) updateData.title = updates.title
    if (updates.status) updateData.status = updates.status
    
    // If any detail fields are updated, reconstruct the details object
    if (updates.leave_type || updates.start_date || updates.end_date || 
        updates.total_days !== undefined || updates.reason !== undefined) {
      const currentDetails = current.details as LeaveDetails | null
      const newDetails: LeaveDetails = {
        leave_type: updates.leave_type || currentDetails?.leave_type || 'annual',
        start_date: updates.start_date || currentDetails?.start_date || '',
        end_date: updates.end_date || currentDetails?.end_date || '',
        total_days: updates.total_days !== undefined ? updates.total_days : (currentDetails?.total_days || 0),
        reason: updates.reason !== undefined ? updates.reason : currentDetails?.reason
      }
      updateData.details = newDetails
    }

    const request = await approvalService.updateApprovalRequest(id, updateData)
    return request
  },

  // Delete leave request
  async deleteLeaveRequest(id: string): Promise<void> {
    await approvalService.deleteApprovalRequest(id)
  },

  // Approve leave request
  async approveLeaveRequest(id: string): Promise<ApprovalRequestWithRelations> {
    const user = authStore.getState().user
    if (!user) throw new Error('User not authenticated')
    
    const request = await approvalService.approveRequest(id, user.id)
    return request
  },

  // Reject leave request
  async rejectLeaveRequest(id: string, reason: string): Promise<ApprovalRequestWithRelations> {
    const user = authStore.getState().user
    if (!user) throw new Error('User not authenticated')
    
    const request = await approvalService.rejectRequest(id, user.id, reason)
    return request
  },

  // Get vacation statistics for a user
  async getVacationStats(userId?: string): Promise<{
    totalDays: number
    usedDays: number
    remainingDays: number
    pendingDays: number
    lastAccrualDate: string | null
  }> {
    const targetUserId = userId || authStore.getState().user?.id
    if (!targetUserId) throw new Error('User ID required')
    
    return approvalService.getUserVacationStats(targetUserId)
  },

  // Get pending leave approvals for current user
  async getPendingApprovals(): Promise<ApprovalRequestWithRelations[]> {
    const user = authStore.getState().user
    if (!user) throw new Error('User not authenticated')
    
    const requests = await approvalService.getPendingApprovals(user.id)
    return requests
      .filter(r => r.type === 'leave')
      .map(r => r as ApprovalRequestWithRelations)
  }
}

// Back-compat alias
export const getAll = leavesApi.getLeaveRequests
;(leavesApi as any).getAll = leavesApi.getLeaveRequests
